import { useEffect, useMemo, useState } from "react";
import { API } from "../../../services/api";
import { contactMatchesFollowUp, formatFollowUpPatient } from "../model/followUpRules";

const CONFIG = { noRedirectOn401: true };

// Filtres par patient et par type de matériel, et ligne sélectionnée dans la vue verticale.
export function useFollowUpFilters(rows) {
    const [patientFilter, setPatientFilter] = useState("");
    const [equipmentTypeFilter, setEquipmentTypeFilter] = useState("");
    const [selectedId, setSelectedId] = useState(null);

    const filteredRows = useMemo(() => {
        const patientQuery = patientFilter.trim().toLocaleLowerCase("fr");
        return rows.filter(row => {
            const patientMatches = !patientQuery
                || formatFollowUpPatient(row).toLocaleLowerCase("fr").includes(patientQuery);
            const equipmentMatches = !equipmentTypeFilter || row.equipmentType === equipmentTypeFilter;
            return patientMatches && equipmentMatches;
        });
    }, [rows, patientFilter, equipmentTypeFilter]);
    const filtersActive = Boolean(patientFilter.trim() || equipmentTypeFilter);
    const selectedRow = filteredRows.find(row => row.id === selectedId) || filteredRows[0] || null;

    useEffect(() => {
        if (selectedRow && selectedRow.id !== selectedId) {
            setSelectedId(selectedRow.id);
        }
    }, [selectedId, selectedRow]);

    const resetFilters = () => {
        setPatientFilter("");
        setEquipmentTypeFilter("");
    };

    return {
        patientFilter, setPatientFilter, equipmentTypeFilter, setEquipmentTypeFilter, resetFilters,
        filteredRows, filtersActive, selectedRow, setSelectedId,
    };
}

// Couleur de la colonne sélectionnée, enregistrée pour tout le service ; restaurée si le serveur la refuse.
export function useColumnColors({ colorsEndpoint, columnColors, setColumnColors, setError }) {
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [savingColumnColor, setSavingColumnColor] = useState(false);

    const saveColumnColors = async nextColors => {
        const previous = columnColors;
        setColumnColors(nextColors);
        setSavingColumnColor(true);
        setError(null);
        try {
            const response = await API.put(colorsEndpoint, nextColors, CONFIG);
            setColumnColors(response.data || {});
        } catch {
            setColumnColors(previous);
            setError("Impossible d'enregistrer la couleur de la colonne.");
        } finally {
            setSavingColumnColor(false);
        }
    };

    const setColumnColor = color => {
        if (!selectedColumn) return;
        saveColumnColors({ ...columnColors, [selectedColumn]: color });
    };

    const clearColumnColor = () => {
        if (!selectedColumn) return;
        const nextColors = { ...columnColors };
        delete nextColors[selectedColumn];
        saveColumnColors(nextColors);
    };

    return { columnColors, selectedColumn, setSelectedColumn, savingColumnColor, setColumnColor, clearColumnColor };
}

// Import Excel du suivi (super-admin) : contrôle à blanc puis import réel, qui recharge le tableau.
export function useFollowUpImport({ endpoint, refresh, setError }) {
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importReport, setImportReport] = useState(null);
    const [importing, setImporting] = useState(false);

    const importWorkbook = async dryRun => {
        if (!importFile) return;
        const form = new FormData();
        form.append("file", importFile);
        setImporting(true);
        setError(null);
        try {
            const response = await API.post(`${endpoint}/import?dryRun=${dryRun}`, form, CONFIG);
            setImportReport(response.data);
            if (!dryRun) await refresh();
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible d'importer ce fichier Excel.");
        } finally {
            setImporting(false);
        }
    };

    const changeFile = file => {
        setImportFile(file);
        setImportReport(null);
    };

    return { importOpen, setImportOpen, importFile, importReport, importing, importWorkbook, changeFile };
}

// Patient ajouté avec son contact éventuel (la ligne est retirée si le contact échoue), identité modifiée sur la
// ligne et sur ses contacts, patient supprimé avec ses contacts.
export function useFollowUpPatientActions({ data, saving, setSelectedId }) {
    const { endpoint, contactsEndpoint, setRows, contacts, setContacts, setError } = data;
    const { setSavingId, patchRow, deleteRow } = saving;
    const [editedPatient, setEditedPatient] = useState(null);
    const [addFormOpen, setAddFormOpen] = useState(false);

    const addRow = async (followUp, contact) => {
        setSavingId("new");
        setError(null);
        let createdFollowUp = null;
        try {
            const { data: created } = await API.post(endpoint, followUp, CONFIG);
            createdFollowUp = created;
            setRows(prev => [created, ...prev]);
            setSelectedId(created.id);
            if (contact) {
                const contactPayload = {
                    ...contact,
                    followUpId: created.id,
                    patient: created.patient,
                    nom: created.patientNom,
                    prenom: created.patientPrenom,
                    notInvoiced: created.notInvoiced,
                    inactive: created.inactive,
                };
                const contactResponse = await API.post(contactsEndpoint, contactPayload, CONFIG);
                setContacts(prev => [contactResponse.data, ...prev]);
            }
            setAddFormOpen(false);
        } catch {
            if (createdFollowUp) {
                await API.delete(`${endpoint}/${createdFollowUp.id}`, CONFIG).catch(() => {});
                setRows(prev => prev.filter(row => row.id !== createdFollowUp.id));
            }
            setError("Impossible d'ajouter le patient.");
        } finally {
            setSavingId(null);
        }
    };

    const savePatient = async patch => {
        const matchingContacts = contacts.filter(contact => contactMatchesFollowUp(contact, editedPatient));
        await patchRow(editedPatient.id, patch);
        if (matchingContacts.length > 0) {
            const updatedContacts = await Promise.all(matchingContacts.map(contact => API.patch(
                `${contactsEndpoint}/${contact.id}`,
                {
                    ...contact,
                    followUpId: editedPatient.id,
                    patient: patch.patient,
                    nom: patch.patientNom,
                    prenom: patch.patientPrenom,
                    notInvoiced: patch.notInvoiced,
                    inactive: patch.inactive,
                },
                CONFIG
            ).then(response => response.data)));
            setContacts(current => current.map(contact => (
                updatedContacts.find(updated => updated.id === contact.id) || contact
            )));
        }
        setEditedPatient(null);
    };

    const deletePatient = async () => {
        await deleteRow(editedPatient.id);
        setContacts(current => current.filter(contact => !contactMatchesFollowUp(contact, editedPatient)));
        setEditedPatient(null);
    };

    return { editedPatient, setEditedPatient, addFormOpen, setAddFormOpen, addRow, savePatient, deletePatient };
}
